import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ColumnOptionsDialog from '../../../uiComponents/ColumnOptionsDialog';

// Autocomplete group headings, listed in the order they should appear.
const DEMOGRAPHIC_GROUP = 'Patient Demographic Fields';
const ANALYTE_GROUP = 'Latest Biochemical Analyte Results';
const CONDITION_GROUP = 'Clinical Diagnosis Results';

// Adapter for the genotype patients table. Normalizes demographic, analyte, and
// clinical-diagnosis inputs into the grouped option shape expected by the shared
// ColumnOptionsDialog and forwards the active fields.
const SelectFieldsDialog = ({
  open,
  onClose,
  fields,
  onSave,
  demographicFields,
  analyteOptions,
  conditionOptions,
  optionsLoading,
}) => {
  const options = useMemo(() => {
    const pool = [];

    (demographicFields || []).forEach((field) => {
      pool.push({
        id: field.id,
        label: field.label,
        group: DEMOGRAPHIC_GROUP,
        field: { id: field.id, label: field.label, type: 'demographic' },
      });
    });

    (analyteOptions || []).forEach((analyte) => {
      if (!analyte?.code) return;
      const shortName = analyte.shortName || analyte.code;
      pool.push({
        id: analyte.code,
        label: `${shortName} Test Result`,
        group: ANALYTE_GROUP,
        field: { id: analyte.code, label: `${shortName} Test Result`, type: 'analyte' },
      });
    });

    (conditionOptions || []).forEach((condition) => {
      if (!condition?.acronym) return;
      const acronym = condition.acronym;
      pool.push({
        id: acronym,
        label: `${acronym} Diagnosis`,
        group: CONDITION_GROUP,
        field: { id: acronym, label: `${acronym} Diagnosis`, type: 'condition' },
      });
    });

    return pool;
  }, [demographicFields, analyteOptions, conditionOptions]);

  return (
    <ColumnOptionsDialog
      open={open}
      onClose={onClose}
      fields={fields}
      options={options}
      optionsLoading={optionsLoading}
      onSave={onSave}
      grouped
    />
  );
};

SelectFieldsDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  onSave: PropTypes.func,
  demographicFields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  analyteOptions: PropTypes.array,
  conditionOptions: PropTypes.array,
  optionsLoading: PropTypes.bool,
};

SelectFieldsDialog.defaultProps = {
  demographicFields: [],
  analyteOptions: [],
  conditionOptions: [],
  optionsLoading: false,
};

export default SelectFieldsDialog;
